import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FiBell,
  FiCalendar,
  FiCheckSquare,
  FiDollarSign,
  FiAlertCircle,
  FiMail,
  FiCheck,
  FiTrash2,
  FiAlertTriangle,
  FiArrowLeft,
  FiX
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { fmtDate } from '../utils/istDate';
import {
  getNotifications,
  getUnreadNotifications,
  getUnreadCount,
  markAsRead,
  markAsUnread,
  markAllAsRead,
  deleteNotification
} from '../finance/api/notifications';

function formatTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return fmtDate(date);
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [activeFilter, setActiveFilter] = useState('ALL'); // 'ALL' | 'UNREAD' | 'LEAVE' | 'TASKS' | 'FINANCE'
  const [page, setPage] = useState(0);

  // Unread count
  const { data: countData } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: getUnreadCount,
  });

  // Query notifications based on active filter
  const { data: pageData, isLoading } = useQuery({
    queryKey: ['notifications', 'page', activeFilter, page],
    queryFn: async () => {
      if (activeFilter === 'UNREAD') {
        return getUnreadNotifications({ page, size: 25 });
      } else if (activeFilter === 'LEAVE') {
        return getNotifications({ page, size: 25, type: 'LEAVE' });
      } else if (activeFilter === 'TASKS') {
        return getNotifications({ page, size: 25, type: 'TASK' });
      } else if (activeFilter === 'FINANCE') {
        return getNotifications({ page, size: 25, type: 'FINANCE' });
      } else {
        return getNotifications({ page, size: 25 });
      }
    },
  });

  const notifications = pageData?.content || (Array.isArray(pageData) ? pageData : []);
  const unreadCount = countData?.unreadCount ?? 0;

  const handleMarkAllRead = async () => {
    try {
      await markAllAsRead();
      toast.success('All notifications marked as read');
      qc.invalidateQueries({ queryKey: ['notifications'] });
    } catch (e) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleToggleRead = async (notif, e) => {
    e.stopPropagation();
    try {
      if (notif.isRead) {
        await markAsUnread(notif.id);
      } else {
        await markAsRead(notif.id);
      }
      qc.invalidateQueries({ queryKey: ['notifications'] });
    } catch (e) {
      toast.error('Failed to update notification');
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await deleteNotification(id);
      toast.success('Notification removed');
      qc.invalidateQueries({ queryKey: ['notifications'] });
    } catch (e) {
      toast.error('Failed to delete notification');
    }
  };

  const handleRowClick = async (notif) => {
    if (!notif.isRead) {
      try {
        await markAsRead(notif.id);
        qc.invalidateQueries({ queryKey: ['notifications'] });
      } catch (e) {}
    }
    if (notif.actionUrl) {
      let url = notif.actionUrl;
      if (url === '/TasksView') url = '/my-space/tasks';
      navigate(url);
    }
  };

  const getIcon = (type, title = '') => {
    const upperType = String(type || '').toUpperCase();
    const upperTitle = String(title || '').toUpperCase();
    if (upperTitle.includes('LOP') || upperTitle.includes('REVOKED') || upperTitle.includes('INSUFFICIENT')) {
      return <FiAlertTriangle className="text-amber-500 text-lg" />;
    }
    if (upperTitle.includes('REJECTED')) {
      return <FiAlertCircle className="text-rose-500 text-lg" />;
    }
    switch (upperType) {
      case 'LEAVE':
        return <FiCalendar className="text-amber-600 text-lg" />;
      case 'TASK':
        return <FiCheckSquare className="text-blue-600 text-lg" />;
      case 'FINANCE':
        return <FiDollarSign className="text-emerald-600 text-lg" />;
      default:
        return <FiBell className="text-slate-500 text-lg" />;
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto pb-12 animate-fade-in">
      {/* Top Header with Exit/Back Button */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-xs">
        <div className="flex items-center gap-3">
          {/* Exit / Back button */}
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-xl border border-slate-200 text-slate-500 hover:text-slate-800 hover:bg-slate-50 transition-colors cursor-pointer"
            title="Exit Notifications"
            aria-label="Exit Notifications"
          >
            <FiArrowLeft size={18} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Notifications</h1>
              {unreadCount > 0 && (
                <span className="px-2.5 py-0.5 text-xs font-bold bg-blue-50 text-blue-700 rounded-full border border-blue-200">
                  {unreadCount} unread
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500 font-medium mt-1">Stay updated with your leave requests, tasks, and system alerts</p>
          </div>
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 text-xs font-bold rounded-xl transition-colors shadow-xs cursor-pointer"
            >
              <FiCheck size={14} /> Mark all as read
            </button>
          )}
          {/* Close / Exit button */}
          <button
            onClick={() => navigate('/my-space/tasks')}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-bold rounded-xl transition-colors cursor-pointer"
            title="Exit to Tasks"
          >
            <FiX size={15} /> Exit
          </button>
        </div>
      </div>

      {/* Filters & Content Box */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
        {/* Filter Tabs */}
        <div className="p-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3 bg-slate-50/50">
          <div className="flex items-center gap-1.5 p-1 bg-slate-100/80 rounded-xl overflow-x-auto max-w-full w-full sm:w-auto">
            {[
              { id: 'ALL', label: 'All' },
              { id: 'UNREAD', label: `Unread (${unreadCount})` },
              { id: 'LEAVE', label: 'Leave' },
              { id: 'TASKS', label: 'Tasks' },
              { id: 'FINANCE', label: 'Finance' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveFilter(tab.id);
                  setPage(0);
                }}
                className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all shrink-0 cursor-pointer ${
                  activeFilter === tab.id
                    ? 'bg-white text-slate-900 shadow-xs'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <span className="text-xs text-slate-500 font-medium">
            Showing {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Notifications List */}
        <div className="divide-y divide-slate-100">
          {isLoading ? (
            <div className="text-center py-16 px-4">
              <span className="text-xs text-slate-400 font-medium">Loading notifications...</span>
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-16 px-4">
              <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-3 text-slate-300 border border-slate-100">
                <FiBell size={24} />
              </div>
              <h3 className="text-base font-bold text-slate-800 mb-1">No notifications found</h3>
              <p className="text-xs text-slate-500">There are no notifications matching your selected filter.</p>
            </div>
          ) : (
            notifications.map(notif => (
              <div
                key={notif.id}
                onClick={() => handleRowClick(notif)}
                className={`group flex items-start gap-4 p-4 hover:bg-slate-50/80 transition-colors cursor-pointer ${
                  !notif.isRead ? 'bg-blue-50/20' : ''
                }`}
              >
                {/* Icon Box */}
                <div className="w-10 h-10 rounded-2xl bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
                  {getIcon(notif.type, notif.title)}
                </div>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className={`text-sm text-slate-800 ${!notif.isRead ? 'font-bold' : 'font-semibold'}`}>
                      {notif.title}
                    </h4>
                    {!notif.isRead && (
                      <span className="w-2 h-2 rounded-full bg-blue-600 shrink-0" title="Unread" />
                    )}
                  </div>
                  <p className={`text-xs mt-1 text-slate-600 leading-relaxed ${!notif.isRead ? 'font-medium text-slate-700' : ''}`}>
                    {notif.message}
                  </p>
                  <span className="text-[11px] text-slate-400 font-medium mt-2 block">
                    {formatTime(notif.createdAt)}
                  </span>
                </div>

                {/* Row Actions */}
                <div className="flex items-center gap-2 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => handleToggleRead(notif, e)}
                    className="px-2.5 py-1 text-[11px] font-bold rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer"
                    title={notif.isRead ? 'Mark as unread' : 'Mark as read'}
                  >
                    {notif.isRead ? 'Mark unread' : 'Mark read'}
                  </button>
                  <button
                    onClick={(e) => handleDelete(notif.id, e)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors cursor-pointer"
                    title="Delete notification"
                  >
                    <FiTrash2 size={14} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
